import { useState, useCallback, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAccounting } from "@/contexts/AccountingContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Mic, MicOff, Loader2, CheckCircle2, XCircle, AlertTriangle,
  Volume2, Sparkles, RotateCcw, Save, FileText, ArrowRight
} from "lucide-react";
import { VoucherType, VoucherLine } from "@/lib/accounting/types";

interface ParsedEntry {
  success: boolean;
  voucherType?: VoucherType;
  partyName?: string;
  amount?: number;
  narration?: string;
  invoiceNumber?: string;
  gstRate?: number;
  cgst?: number;
  sgst?: number;
  igst?: number;
  date?: string;
  debitLedgerName?: string;
  creditLedgerName?: string;
  confidence?: number;
  clarification?: string;
}

type Step = "idle" | "listening" | "processing" | "confirm" | "saved" | "error";

// Web Speech API types
interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
}
interface SpeechRecognitionErrorEvent extends Event {
  error: string;
}

const EXAMPLE_COMMANDS = [
  '"Create invoice for Rahul Traders ₹10,000"',
  '"Received payment ₹25,000 from Sharma Electronics"',
  '"Purchase from Delhi Suppliers ₹50,000 with 18% GST"',
  '"Transfer ₹1 lakh from HDFC to cash"',
  '"Record office rent expense ₹15,000"',
  '"Create credit note for Gupta & Sons ₹5,000"',
];

export default function VoiceAccountingPage() {
  const { ledgers, createVoucher, getLedger } = useAccounting();
  const [step, setStep] = useState<Step>("idle");
  const [transcript, setTranscript] = useState("");
  const [interimTranscript, setInterimTranscript] = useState("");
  const [parsed, setParsed] = useState<ParsedEntry | null>(null);
  const [editedEntry, setEditedEntry] = useState<ParsedEntry | null>(null);
  const [pulseAnim, setPulseAnim] = useState(false);
  const recognitionRef = useRef<any>(null);

  // Check browser support
  const speechSupported = typeof window !== "undefined" &&
    ("SpeechRecognition" in window || "webkitSpeechRecognition" in window);

  const startListening = useCallback(() => {
    if (!speechSupported) {
      toast.error("Speech recognition is not supported in this browser. Use Chrome or Edge.");
      return;
    }

    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.lang = "en-IN";
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      setStep("listening");
      setTranscript("");
      setInterimTranscript("");
      setParsed(null);
      setEditedEntry(null);
      setPulseAnim(true);
    };

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let final = "";
      let interim = "";
      for (let i = 0; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          final += result[0].transcript;
        } else {
          interim += result[0].transcript;
        }
      }
      if (final) setTranscript(final);
      setInterimTranscript(interim);
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      console.error("Speech error:", event.error);
      setPulseAnim(false);
      if (event.error === "no-speech") {
        toast.error("No speech detected. Please try again.");
        setStep("idle");
      } else if (event.error === "not-allowed") {
        toast.error("Microphone access denied. Please enable it in browser settings.");
        setStep("idle");
      } else {
        setStep("error");
      }
    };

    recognition.onend = () => {
      setPulseAnim(false);
    };

    recognitionRef.current = recognition;
    recognition.start();
  }, [speechSupported]);

  const stopListening = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    setPulseAnim(false);
  }, []);

  // When transcript is finalized, send to AI
  useEffect(() => {
    if (transcript && step === "listening") {
      stopListening();
      processTranscript(transcript);
    }
  }, [transcript, step]);

  const processTranscript = async (text: string) => {
    setStep("processing");
    try {
      const ledgerNames = ledgers.map(l => l.name);
      const { data, error } = await supabase.functions.invoke("voice-accounting", {
        body: { transcript: text, ledgerNames },
      });
      if (error) throw error;
      if (data.error) { toast.error(data.error); setStep("error"); return; }

      const result = data as ParsedEntry;
      setParsed(result);
      setEditedEntry({ ...result });

      if (result.success && result.confidence && result.confidence >= 50) {
        setStep("confirm");
      } else {
        setStep("error");
        toast.error(result.clarification || "Could not understand the command. Please try again.");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to process voice command.");
      setStep("error");
    }
  };

  const handleSave = useCallback(async () => {
    if (!editedEntry || !editedEntry.voucherType || !editedEntry.amount) return;

    // Find or use default ledgers
    const findLedger = (name?: string, fallbackGroup?: string) => {
      if (name) {
        const match = ledgers.find(l => l.name.toLowerCase().includes(name.toLowerCase()));
        if (match) return match;
      }
      if (fallbackGroup) {
        return ledgers.find(l => l.group === fallbackGroup);
      }
      return undefined;
    };

    const debitLedger = findLedger(editedEntry.debitLedgerName, editedEntry.voucherType === "Sales" ? "Sundry Debtors" : editedEntry.voucherType === "Payment" ? "Sundry Creditors" : "Bank Accounts");
    const creditLedger = findLedger(editedEntry.creditLedgerName, editedEntry.voucherType === "Sales" ? "Sales Accounts" : editedEntry.voucherType === "Purchase" ? "Sundry Creditors" : "Bank Accounts");

    if (!debitLedger || !creditLedger) {
      toast.error("Could not map to valid ledgers. Please create the required ledgers first.");
      return;
    }

    const lines: VoucherLine[] = [
      { id: `vl-1`, ledgerId: debitLedger.id, ledgerName: debitLedger.name, debit: editedEntry.amount, credit: 0 },
      { id: `vl-2`, ledgerId: creditLedger.id, ledgerName: creditLedger.name, debit: 0, credit: editedEntry.amount },
    ];

    const partyLedger = editedEntry.partyName ? ledgers.find(l => l.name.toLowerCase().includes(editedEntry.partyName!.toLowerCase())) : undefined;

    const result = await createVoucher(
      editedEntry.voucherType,
      lines,
      editedEntry.narration || `Voice entry: ${transcript}`,
      partyLedger?.id,
      editedEntry.invoiceNumber,
      (editedEntry.cgst || 0) + (editedEntry.sgst || 0) + (editedEntry.igst || 0),
      editedEntry.cgst,
      editedEntry.sgst,
      editedEntry.igst
    );

    if (result.success) {
      setStep("saved");
      toast.success(`Voucher ${result.voucher?.voucherNumber} created successfully!`);
    } else {
      toast.error(result.error || "Failed to create voucher.");
    }
  }, [editedEntry, ledgers, createVoucher, transcript]);

  const reset = () => {
    setStep("idle");
    setTranscript("");
    setInterimTranscript("");
    setParsed(null);
    setEditedEntry(null);
  };

  const confidenceColor = (c: number) => c >= 80 ? "text-success" : c >= 60 ? "text-warning" : "text-destructive";

  return (
    <div className="space-y-6 max-w-3xl mx-auto">
      <div>
        <h1 className="text-2xl font-bold font-display text-foreground">Voice Accounting</h1>
        <p className="text-sm text-muted-foreground">Speak your accounting entries — AI converts them instantly</p>
      </div>

      {/* Main Voice Interface */}
      <div className="p-6 rounded-xl bg-card border border-border shadow-card text-center">
        {/* Microphone Button */}
        <div className="flex justify-center mb-6">
          <button
            onClick={step === "listening" ? stopListening : startListening}
            disabled={step === "processing"}
            className="relative"
          >
            <motion.div
              className={`w-24 h-24 rounded-full flex items-center justify-center transition-colors ${
                step === "listening" ? "bg-destructive" : "bg-accent"
              } ${step === "processing" ? "opacity-50" : "hover:opacity-90"}`}
              animate={pulseAnim ? { scale: [1, 1.1, 1] } : {}}
              transition={pulseAnim ? { repeat: Infinity, duration: 1.5 } : {}}
            >
              {step === "processing" ? (
                <Loader2 className="h-10 w-10 text-accent-foreground animate-spin" />
              ) : step === "listening" ? (
                <MicOff className="h-10 w-10 text-destructive-foreground" />
              ) : (
                <Mic className="h-10 w-10 text-accent-foreground" />
              )}
            </motion.div>
            {pulseAnim && (
              <motion.div
                className="absolute inset-0 rounded-full border-2 border-destructive"
                animate={{ scale: [1, 1.5], opacity: [0.6, 0] }}
                transition={{ repeat: Infinity, duration: 1.5 }}
              />
            )}
          </button>
        </div>

        {/* Status */}
        <p className="text-sm font-medium text-foreground mb-1">
          {step === "idle" && "Tap the mic and speak your command"}
          {step === "listening" && "Listening... speak now"}
          {step === "processing" && "AI is parsing your command..."}
          {step === "confirm" && "Review and confirm your entry"}
          {step === "saved" && "Entry saved successfully!"}
          {step === "error" && "Something went wrong"}
        </p>

        {/* Live Transcript */}
        {(step === "listening" || interimTranscript) && (
          <div className="mt-3 p-3 rounded-lg bg-muted/30 border border-border/50">
            <div className="flex items-center gap-2 mb-1">
              <Volume2 className="h-3.5 w-3.5 text-accent" />
              <span className="text-xs text-muted-foreground">Live transcript</span>
            </div>
            <p className="text-sm text-foreground italic">
              {transcript || interimTranscript || "..."}
            </p>
          </div>
        )}

        {/* Final Transcript */}
        {transcript && step !== "listening" && (
          <div className="mt-3 p-3 rounded-lg bg-accent/5 border border-accent/20">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="h-3.5 w-3.5 text-accent" />
              <span className="text-xs text-muted-foreground">You said</span>
            </div>
            <p className="text-sm font-medium text-foreground">"{transcript}"</p>
          </div>
        )}
      </div>

      {/* Confirmation Panel */}
      <AnimatePresence mode="wait">
        {step === "confirm" && editedEntry && (
          <motion.div
            key="confirm"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            className="p-5 rounded-xl bg-card border-2 border-accent/30 shadow-card space-y-4"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-accent" />
                <h3 className="text-sm font-semibold text-foreground">Confirm Entry</h3>
              </div>
              <Badge variant="outline" className={`text-xs ${confidenceColor(editedEntry.confidence || 0)}`}>
                {editedEntry.confidence}% confidence
              </Badge>
            </div>

            {/* Editable Fields */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Voucher Type</label>
                <Select
                  value={editedEntry.voucherType}
                  onValueChange={(v) => setEditedEntry(prev => prev ? { ...prev, voucherType: v as VoucherType } : prev)}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Sales", "Purchase", "Receipt", "Payment", "Journal", "Contra", "Credit Note", "Debit Note"].map(t => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Amount (₹)</label>
                <Input
                  type="number"
                  value={editedEntry.amount || ""}
                  onChange={(e) => setEditedEntry(prev => prev ? { ...prev, amount: parseFloat(e.target.value) || 0 } : prev)}
                />
              </div>

              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Party Name</label>
                <Input
                  value={editedEntry.partyName || ""}
                  onChange={(e) => setEditedEntry(prev => prev ? { ...prev, partyName: e.target.value } : prev)}
                />
              </div>

              <div>
                <label className="text-xs text-muted-foreground mb-1 block">Date</label>
                <Input
                  type="date"
                  value={editedEntry.date || new Date().toISOString().split("T")[0]}
                  onChange={(e) => setEditedEntry(prev => prev ? { ...prev, date: e.target.value } : prev)}
                />
              </div>

              <div className="sm:col-span-2">
                <label className="text-xs text-muted-foreground mb-1 block">Narration</label>
                <Input
                  value={editedEntry.narration || ""}
                  onChange={(e) => setEditedEntry(prev => prev ? { ...prev, narration: e.target.value } : prev)}
                />
              </div>

              {editedEntry.invoiceNumber && (
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Invoice Number</label>
                  <Input
                    value={editedEntry.invoiceNumber || ""}
                    onChange={(e) => setEditedEntry(prev => prev ? { ...prev, invoiceNumber: e.target.value } : prev)}
                  />
                </div>
              )}

              {(editedEntry.cgst || editedEntry.sgst || editedEntry.igst) && (
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">GST</label>
                  <div className="text-sm text-foreground">
                    {editedEntry.cgst ? `CGST: ₹${editedEntry.cgst.toLocaleString("en-IN")} ` : ""}
                    {editedEntry.sgst ? `SGST: ₹${editedEntry.sgst.toLocaleString("en-IN")} ` : ""}
                    {editedEntry.igst ? `IGST: ₹${editedEntry.igst.toLocaleString("en-IN")}` : ""}
                  </div>
                </div>
              )}
            </div>

            {/* Ledger Mapping Preview */}
            <div className="p-3 rounded-lg bg-muted/30 border border-border/50">
              <p className="text-xs text-muted-foreground mb-2">Ledger Mapping</p>
              <div className="flex items-center gap-2 text-sm">
                <span className="px-2 py-1 rounded bg-success/10 text-success font-medium text-xs">Dr: {editedEntry.debitLedgerName || "Auto"}</span>
                <ArrowRight className="h-3 w-3 text-muted-foreground" />
                <span className="px-2 py-1 rounded bg-destructive/10 text-destructive font-medium text-xs">Cr: {editedEntry.creditLedgerName || "Auto"}</span>
              </div>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3 pt-2">
              <Button onClick={handleSave} className="flex-1 gap-2">
                <Save className="h-4 w-4" /> Save Entry
              </Button>
              <Button variant="outline" onClick={reset} className="gap-2">
                <RotateCcw className="h-4 w-4" /> Start Over
              </Button>
            </div>
          </motion.div>
        )}

        {step === "saved" && (
          <motion.div
            key="saved"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="p-6 rounded-xl bg-success/5 border border-success/20 text-center"
          >
            <CheckCircle2 className="h-12 w-12 text-success mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-foreground mb-1">Entry Created!</h3>
            <p className="text-sm text-muted-foreground mb-4">Your voice command has been converted to an accounting entry.</p>
            <Button onClick={reset} className="gap-2">
              <Mic className="h-4 w-4" /> Record Another
            </Button>
          </motion.div>
        )}

        {step === "error" && (
          <motion.div
            key="error"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="p-6 rounded-xl bg-destructive/5 border border-destructive/20 text-center"
          >
            <XCircle className="h-12 w-12 text-destructive mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-foreground mb-1">Could Not Parse</h3>
            <p className="text-sm text-muted-foreground mb-4">
              {parsed?.clarification || "Try speaking more clearly with the amount and party name."}
            </p>
            <Button onClick={reset} variant="outline" className="gap-2">
              <RotateCcw className="h-4 w-4" /> Try Again
            </Button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Example Commands */}
      {step === "idle" && (
        <div className="p-5 rounded-xl bg-card border border-border">
          <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-accent" />
            Example Voice Commands
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {EXAMPLE_COMMANDS.map((cmd, i) => (
              <div key={i} className="p-2.5 rounded-lg bg-muted/20 border border-border/50 text-xs text-muted-foreground italic">
                {cmd}
              </div>
            ))}
          </div>

          {!speechSupported && (
            <div className="mt-3 p-3 rounded-lg bg-warning/10 border border-warning/20 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-warning flex-shrink-0" />
              <p className="text-xs text-warning">
                Speech recognition is not supported in this browser. Please use Chrome, Edge, or Safari.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
